import { useTranslation } from 'react-i18next'
import { Befehlsnachricht } from '@/models/Befehlsnachricht'
import { CalendarIcon20, ScheduleIcon20 } from '@repo/ui/icons'
import { formatDate, formatTime } from '@repo/ui'
import { ComponentProps } from 'react'
import { cn } from '@/utils/classnames'

interface BfnKopfdatenBoxProps extends ComponentProps<'div'> {
  bfn: Befehlsnachricht
  'data-testid': string
}

export default function BfnKopfdatenBox({
  bfn,
  className,
  'data-testid': testId,
  ...props
}: Readonly<BfnKopfdatenBoxProps>) {
  const { t } = useTranslation('bfn')

  return (
    <div className={cn('flex flex-col w-full', className)} {...props}>
      <div className="text-xs" data-testid={`${testId}-label`}>
        {t('kopfdaten.label')}
      </div>
      <div className="p-1 border rounded border-gray-300 dark:border-gray-700">
        <div className="grid grid-cols-2 gap-1">
          {/* Eindeutige Kennung */}
          <div className="flex flex-col">
            <span
              className="text-sm"
              data-testid={`${testId}-eindeutigeKennung-label`}
            >
              {t('kopfdaten.fields.eindeutigeKennung')}
            </span>
            <span
              className="font-bold"
              data-testid={`${testId}-eindeutigeKennung`}
            >
              {bfn.eindeutigeKennung}
            </span>
          </div>

          {/* Art der Fahrt */}
          <div className="flex flex-col">
            <span
              className="text-sm"
              data-testid={`${testId}-art-der-fahrt-label`}
            >
              {t('kopfdaten.fields.art_der_fahrt')}
            </span>
            <span className="font-bold" data-testid={`${testId}-art-der-fahrt`}>
              {t(`art_der_fahrt.${bfn.artDerFahrt}`)}
            </span>
          </div>

          {/* Zugnummer */}
          <div className="flex flex-col">
            <span className="text-sm" data-testid={`${testId}-zugnummer-label`}>
              {t('kopfdaten.fields.zugnummer')}
            </span>
            <span className="font-bold" data-testid={`${testId}-zugnummer`}>
              {bfn.zugnummer}
            </span>
          </div>

          {/* Fahrdienstleiter Arbeitsplatz */}
          <div className="flex flex-col">
            <span
              className="text-sm"
              data-testid={`${testId}-fdl-arbeitsplatz-label`}
            >
              {t('kopfdaten.fields.fdlArbeitsplatz')}
            </span>
            <span
              className="font-bold"
              data-testid={`${testId}-fdl-arbeitsplatz`}
            >
              {bfn.fdlArbeitsplatz}
            </span>
          </div>

          {/* Fahrdienstleiter Arbeitsort */}
          <div className="flex flex-col">
            <span
              className="text-sm"
              data-testid={`${testId}-fdl-arbeitsplatz-ort-label`}
            >
              {t('kopfdaten.fields.fdlArbeitsplatzOrt')}
            </span>
            <span
              className="font-bold"
              data-testid={`${testId}-fdl-arbeitsplatz-ort`}
            >
              {bfn.fdlArbeitsplatzOrt}
            </span>
          </div>

          {/* Versandzeitpunkt */}
          <div className="flex flex-col col-span-2">
            <div
              className="text-xs"
              data-testid={`${testId}-versandzeitpunkt-label`}
            >
              {t('kopfdaten.fields.versandzeitpunkt')}
            </div>
            <div className="flex gap-1">
              <span className="flex items-center">
                <CalendarIcon20
                  data-testid={`${testId}-versandzeitpunkt-date-icon`}
                />
                <span
                  className="pl-0.5"
                  data-testid={`${testId}-versandzeitpunkt-date`}
                >
                  {formatDate(bfn.versandzeitpunkt)}
                </span>
              </span>
              <span className="flex items-center">
                <ScheduleIcon20
                  className="shrink-0"
                  data-testid={`${testId}-versandzeitpunkt-time-icon`}
                />
                <span
                  className="pl-0.5"
                  data-testid={`${testId}-versandzeitpunkt-time`}
                >
                  {formatTime(bfn.versandzeitpunkt)}
                </span>
              </span>
            </div>
          </div>

          {/* Quittierungsdatum */}
          <div className="flex flex-col col-span-2">
            <div
              className="text-xs"
              data-testid={`${testId}-quittierungsdatum-label`}
            >
              {t('kopfdaten.fields.quittierungsdatum')}
            </div>
            <div className="flex gap-1">
              <span className="flex items-center">
                <CalendarIcon20
                  data-testid={`${testId}-quittierungsdatum-date-icon`}
                />
                <span
                  className="pl-0.5"
                  data-testid={`${testId}-quittierungsdatum-date`}
                >
                  {bfn.quittierungsdatum
                    ? formatDate(bfn.quittierungsdatum)
                    : '-'}
                </span>
              </span>
              <span className="flex items-center">
                <ScheduleIcon20
                  data-testid={`${testId}-quittierungsdatum-time-icon`}
                />
                <span
                  className="pl-0.5"
                  data-testid={`${testId}-quittierungsdatum-time`}
                >
                  {bfn.quittierungsdatum
                    ? formatTime(bfn.quittierungsdatum)
                    : '-'}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
